import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AppFeatures = () => {
  const { t } = useLanguage();
  
  const featureCards = t('appFeatures.cards', []);
  const cards = Array.isArray(featureCards) ? featureCards : [];
  const featureIcons = [
    (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    )
  ];

  const prayerToolsCopy = t('appFeatures.toolsList', []);
  const prayerTools = Array.isArray(prayerToolsCopy) ? prayerToolsCopy : [];

  return (
    <section id="features" className="section-padding bg-white">
      <div className="container-max">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-500 mb-6">
              {t('appFeatures.title')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('appFeatures.subtitle')}
            </p>
          </div>

          {/* Main Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {cards.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl border-2 border-green-500 p-8 hover:bg-green-500 hover:text-white transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center text-white mb-6">
                  {featureIcons[index % featureIcons.length]}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                {Array.isArray(feature.details) && (
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start text-sm text-gray-500">
                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* Prayer Tools Section */}
            <div className="bg-white rounded-2xl border-2 border-green-500 p-8">
            <div className="text-center mb-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                {t('appFeatures.prayerTools.title')}
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('appFeatures.prayerTools.intro')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prayerTools.map((tool, index) => (
                <div key={index} className="bg-white rounded-xl p-6 hover:bg-green-500 hover:text-white transition-all duration-300 border-2 border-green-500">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white mb-4">
                    {tool.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    {tool.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {tool.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-green-500 rounded-2xl p-8 text-white">
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">
                {t('appFeatures.cta.title')}
              </h3>
              <p className="text-lg mb-6 opacity-90">
                {t('appFeatures.cta.subtitle')}
              </p>
              <a
                href="https://expo.dev/artifacts/eas/5xguG1EpDELXbKzQw8eseM.apk"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-white text-green-500 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-300"
              >
                {t('appFeatures.cta.button')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppFeatures;
